import { JSX, Fragment, useMemo, useCallback } from "react";
import { useState } from "@vrf/fr-vrf-core-react";
import {
  FrVrfSingleLineInputField,
  FrVrfMultipleLineInputField,
  FrVrfNumberInputField,
  FrVrfSingleLineDisplayField,
  FrVrfSingleLinkDisplayField,
  FrVrfTextDisplayField,
  FrVrfInputDatepickerField,
  FrVrfInputDaterangeField,
  FrVrfCheckboxField,
  FrVrfCheckboxesField,
  FrVrfDropdownField,
  FrVrfRadioButtonField,
  FrVrfSelectorInputField,
  FrVrfCodeInputSearchField,
  FrVrfMultipleSelectorField,
  FrVrfIcon,
  FrVrfSearchResultSummary,
  FrVrfTab,
  FrVrfTitle,
  FrVrfFieldLabel,
  FrVrfPanel,
  FrVrfActionList,
  FrVrfAlertBar,
  FrVrfFileUploadField,
  FrVrfLoading,
  FrVrfHorizontalSeparator,
  FrVrfListBuilder,
  FrVrfAccordion,
  FrVrfTags,
  FrVrfButtonPanel,
  FrVrfColumnPanel,
  FrVrfFormPanel,
  FrVrfFooter,
  FrVrfButton,
} from "@vrf/fr-vrf-web-components-react";

import { useGlobalNotifier } from "@vrf/fr-vrf-core-react";

import { WebComponentsConstants } from "./WebComponentsConstants";

require("./WebComponentsPageContent.css");

export const WebComponentsPageContent = () => {
  const { showToast, showConfirmationDialog } = useGlobalNotifier();
  const showToastMessage = (
    message: string,
    design?: "alert" | "completion",
  ) => {
    showToast({
      design: design || "completion",
      message: message,
    });
  };

  const frVrfSingleLineInputField: Component = {
    name: "Single Line Input Field",
    tagName: "<FrVrfSingleLineInputField>",
    samples: [
      {
        description: "Single Line Input Field (default)",
        Sample: () => {
          const [inputValue, setInputValue] = useState<string>();
          const onInputValueChanged = useCallback((evt: CustomEvent) => {
            setInputValue(evt.detail.value);
          }, []);
          return (
            <FrVrfSingleLineInputField
              labelContent="label label label"
              content={inputValue}
              onInputValueChanged={onInputValueChanged}
            />
          );
        },
      },
      {
        description: "Single Line Input Field (labelPosition)",
        Sample: () => {
          const [inputValue, setInputValue] =
            useState<string>("test test test");
          const onInputValueChanged = useCallback((evt: CustomEvent) => {
            setInputValue(evt.detail.value);
          }, []);
          return (
            <FrVrfSingleLineInputField
              labelContent="label label label"
              content={inputValue}
              labelPosition="top"
              onInputValueChanged={onInputValueChanged}
            />
          );
        },
      },
      {
        description: "Single Line Input Field (placeholder)",
        Sample: () => {
          const [inputValue, setInputValue] = useState<string>();
          const onInputValueChanged = useCallback((evt: CustomEvent) => {
            setInputValue(evt.detail.value);
          }, []);
          return (
            <FrVrfSingleLineInputField
              placeholder="placeholder"
              labelContent="label label label"
              content={inputValue}
              onInputValueChanged={onInputValueChanged}
            />
          );
        },
      },
      {
        description: "Single Line Input Field (required)",
        Sample: () => {
          const [inputValue, setInputValue] =
            useState<string>("test test test");
          const onInputValueChanged = useCallback((evt: CustomEvent) => {
            setInputValue(evt.detail.value);
          }, []);
          return (
            <FrVrfSingleLineInputField
              labelContent="label label label"
              content={inputValue}
              required
              onInputValueChanged={onInputValueChanged}
            />
          );
        },
      },
      {
        description: "Single Line Input Field (disabled)",
        Sample: () => {
          const [inputValue, setInputValue] =
            useState<string>("test test test");
          const onInputValueChanged = useCallback((evt: CustomEvent) => {
            setInputValue(evt.detail.value);
          }, []);
          return (
            <FrVrfSingleLineInputField
              disabled
              labelContent="label label label"
              content={inputValue}
              onInputValueChanged={onInputValueChanged}
            />
          );
        },
      },
      {
        description: "Single Line Input Field (clearButtonVisible)",
        Sample: () => {
          const [inputValue, setInputValue] =
            useState<string>("test test test");
          const onInputValueChanged = useCallback((evt: CustomEvent) => {
            setInputValue(evt.detail.value);
          }, []);
          return (
            <FrVrfSingleLineInputField
              clearButtonVisible
              labelContent="label label label"
              content={inputValue}
              onInputValueChanged={onInputValueChanged}
            />
          );
        },
      },
      {
        description: "Single Line Input Field (errorMessage)",
        Sample: () => {
          const [inputValue, setInputValue] = useState<string>(
            "test test test test test",
          );
          const onInputValueChanged = useCallback((evt: CustomEvent) => {
            setInputValue(evt.detail.value);
          }, []);
          return (
            <FrVrfSingleLineInputField
              labelContent="label label label"
              content={inputValue}
              errorMessage="size must be greater than 12."
              onInputValueChanged={onInputValueChanged}
            />
          );
        },
      },
      {
        description: "Single Line Input Field (iconName)",
        Sample: () => {
          const [inputValue, setInputValue] =
            useState<string>("test test test");
          const onInputValueChanged = useCallback((evt: CustomEvent) => {
            setInputValue(evt.detail.value);
          }, []);
          return (
            <FrVrfSingleLineInputField
              labelContent="label label label"
              content={inputValue}
              iconName="frIcon:search"
              onIconClicked={() => {
                alert("icon click");
              }}
              onInputValueChanged={onInputValueChanged}
            />
          );
        },
      },
      {
        description: "Single Line Input Field (textAlign)",
        Sample: () => {
          const [inputValue, setInputValue] =
            useState<string>("test test test");
          const onInputValueChanged = useCallback((evt: CustomEvent) => {
            setInputValue(evt.detail.value);
          }, []);
          return (
            <FrVrfSingleLineInputField
              textAlign="right"
              labelContent="label label label"
              content={inputValue}
              onInputValueChanged={onInputValueChanged}
            />
          );
        },
      },
      {
        description: "Single Line Input Field (style)",
        Sample: () => {
          const [inputValue, setInputValue] = useState<string>(
            "test test test test test",
          );
          const onInputValueChanged = useCallback((evt: CustomEvent) => {
            setInputValue(evt.detail.value);
          }, []);
          return (
            <FrVrfSingleLineInputField
              style={{
                "--vrf-single-line-input-field-label-width": "max-content",
                "--vrf-single-line-input-field-label-font-size": "5px",
                "--vrf-single-line-input-field-icon-size": "40px",
                "--vrf-single-line-input-field-font-size": "25px",
                "--vrf-single-line-input-field-input-width": "500px",
              }}
              labelContent="label label label"
              iconName="frIcon:search"
              content={inputValue}
              errorMessage="size must be greater than 12."
              onInputValueChanged={onInputValueChanged}
            />
          );
        },
      },
      {
        description: "Single Line Input Field (information)",
        Sample: () => {
          const [inputValue, setInputValue] = useState<string>("");
          const onInputValueChanged = useCallback((evt: CustomEvent) => {
            setInputValue(evt.detail.value);
          }, []);
          return (
            <FrVrfSingleLineInputField
              style={{
                "--vrf-single-line-input-field-label-width": "max-content",
                "--vrf-single-line-input-field-label-font-size": "5px",
                "--vrf-single-line-input-field-icon-size": "40px",
                "--vrf-single-line-input-field-font-size": "25px",
                "--vrf-single-line-input-field-input-width": "500px",
              }}
              labelContent="label label label"
              content={inputValue}
              onInputValueChanged={onInputValueChanged}
              information="input information"
            />
          );
        },
      },
      {
        description: "Single Line Input Field (constraint)",
        Sample: () => {
          const [inputValue, setInputValue] =
            useState<string>("TEST TEST TEST");
          const onInputValueChanged = useCallback((evt: CustomEvent) => {
            setInputValue(evt.detail.value);
          }, []);
          return (
            <FrVrfSingleLineInputField
              labelContent="label label label"
              content={inputValue}
              onInputValueChanged={onInputValueChanged}
              constraint={`[Size(${inputValue}, {min: 1, max: 5}), IsLowerAlphabet(${inputValue})]`}
            />
          );
        },
      },
    ],
  };

  const frVrfMultipleLineInputField: Component = {
    name: "Multiple Line Input Field",
    tagName: "<FrVrfMultipleLineInputField>",
    samples: [
      {
        description: "Multiple Line Input Field (default)",
        Sample: () => {
          const [inputValue, setInputValue] = useState<string>();
          const onInputValueChanged = useCallback((evt: CustomEvent) => {
            setInputValue(evt.detail.value);
          }, []);
          return (
            <FrVrfMultipleLineInputField
              labelContent="label label label"
              content={inputValue}
              onInputValueChanged={onInputValueChanged}
            />
          );
        },
      },
      {
        description: "Multiple Line Input Field (labelPosition)",
        Sample: () => {
          const [inputValue, setInputValue] = useState<string>();
          const onInputValueChanged = useCallback((evt: CustomEvent) => {
            setInputValue(evt.detail.value);
          }, []);
          return (
            <FrVrfMultipleLineInputField
              labelContent="label label label"
              labelPosition="top"
              content={inputValue}
              onInputValueChanged={onInputValueChanged}
            />
          );
        },
      },
      {
        description: "Multiple Line Input Field (placeholder)",
        Sample: () => {
          const [inputValue, setInputValue] = useState<string>();
          const onInputValueChanged = useCallback((evt: CustomEvent) => {
            setInputValue(evt.detail.value);
          }, []);
          return (
            <FrVrfMultipleLineInputField
              labelContent="label label label"
              placeholder="placeholder."
              content={inputValue}
              onInputValueChanged={onInputValueChanged}
            />
          );
        },
      },
      {
        description: "Multiple Line Input Field (required)",
        Sample: () => {
          const [inputValue, setInputValue] = useState<string>();
          const onInputValueChanged = useCallback((evt: CustomEvent) => {
            setInputValue(evt.detail.value);
          }, []);
          return (
            <FrVrfMultipleLineInputField
              labelContent="label label label"
              content={inputValue}
              required
              onInputValueChanged={onInputValueChanged}
            />
          );
        },
      },
      {
        description: "Multiple Line Input Field (disabled)",
        Sample: () => {
          const [inputValue, setInputValue] = useState<string>();
          const onInputValueChanged = useCallback((evt: CustomEvent) => {
            setInputValue(evt.detail.value);
          }, []);
          return (
            <FrVrfMultipleLineInputField
              labelContent="label label label"
              content={inputValue}
              disabled
              onInputValueChanged={onInputValueChanged}
            />
          );
        },
      },
      {
        description: "Multiple Line Input Field (clearButtonVisible)",
        Sample: () => {
          const [inputValue, setInputValue] =
            useState<string>("test test test");
          const onInputValueChanged = useCallback((evt: CustomEvent) => {
            setInputValue(evt.detail.value);
          }, []);
          return (
            <FrVrfMultipleLineInputField
              labelContent="label label label"
              content={inputValue}
              clearButtonVisible={true}
              onInputValueChanged={onInputValueChanged}
            />
          );
        },
      },
      {
        description: "Multiple Line Input Field (errorMessage)",
        Sample: () => {
          const [inputValue, setInputValue] = useState<string>();
          const onInputValueChanged = useCallback((evt: CustomEvent) => {
            setInputValue(evt.detail.value);
          }, []);
          return (
            <FrVrfMultipleLineInputField
              labelContent="label label label"
              content={inputValue}
              errorMessage="Please input only English."
              onInputValueChanged={onInputValueChanged}
            />
          );
        },
      },
      {
        description: "Multiple Line Input Field (resize)",
        Sample: () => {
          const [inputValue, setInputValue] = useState<string>();
          const onInputValueChanged = useCallback((evt: CustomEvent) => {
            setInputValue(evt.detail.value);
          }, []);
          return (
            <FrVrfMultipleLineInputField
              labelContent="label label label"
              content={inputValue}
              resize="both"
              onInputValueChanged={onInputValueChanged}
            />
          );
        },
      },
      {
        description: "Multiple Line Input Field (style)",
        Sample: () => {
          const [inputValue, setInputValue] = useState<string>();
          const onInputValueChanged = useCallback((evt: CustomEvent) => {
            setInputValue(evt.detail.value);
          }, []);
          return (
            <FrVrfMultipleLineInputField
              style={{
                "--vrf-multiple-line-input-field-label-width": "30px",
                "--vrf-multiple-line-input-field-label-font-size": "5px",
                "--vrf-multiple-line-input-field-input-height": "400px",
                "--vrf-multiple-line-input-field-input-width": "300px",
                "--vrf-multiple-line-input-field-font-size": "20px",
              }}
              labelContent="label label label"
              content={inputValue}
              errorMessage="Please input only English."
              onInputValueChanged={onInputValueChanged}
            />
          );
        },
      },
      {
        description: "Multiple Line Input Field (iconName)",
        Sample: () => {
          const [inputValue, setInputValue] = useState<string>();
          const onInputValueChanged = useCallback((evt: CustomEvent) => {
            setInputValue(evt.detail.value);
          }, []);
          return (
            <FrVrfMultipleLineInputField
              labelContent="label label label"
              content={inputValue}
              resize="both"
              iconName="frIcon:search"
              onInputValueChanged={onInputValueChanged}
            />
          );
        },
      },
      {
        description: "Multiple Line Input Field (isListContent)",
        Sample: () => {
          const [inputValue, setInputValue] = useState<string>(
            "test1\ntest2\ntest3\ntest4",
          );
          const onInputValueChanged = useCallback((evt: CustomEvent) => {
            setInputValue(evt.detail.value);
          }, []);
          return (
            <FrVrfMultipleLineInputField
              labelContent="label label label"
              content={inputValue}
              isListContent
              onInputValueChanged={onInputValueChanged}
            />
          );
        },
      },
      {
        description: "Multiple Line Input Field (information)",
        Sample: () => {
          const [inputValue, setInputValue] = useState<string>();
          const onInputValueChanged = useCallback((evt: CustomEvent) => {
            setInputValue(evt.detail.value);
          }, []);
          return (
            <FrVrfMultipleLineInputField
              labelContent="label"
              content={inputValue}
              onInputValueChanged={onInputValueChanged}
              information="information"
            />
          );
        },
      },
      {
        description: "Multiple Line Input Field (constraint)",
        Sample: () => {
          const [inputValue, setInputValue] = useState<string>();
          const onInputValueChanged = useCallback((evt: CustomEvent) => {
            setInputValue(evt.detail.value);
          }, []);
          return (
            <FrVrfMultipleLineInputField
              labelContent="label"
              content={inputValue}
              onInputValueChanged={onInputValueChanged}
              constraint={`IsNotEmpty(${inputValue})`}
            />
          );
        },
      },
      {
        description: "Multiple Line Input Field (memberConstraint)",
        Sample: () => {
          const [inputValue, setInputValue] = useState<string>(
            "12345\ntest test test",
          );
          const onInputValueChanged = useCallback((evt: CustomEvent) => {
            setInputValue(evt.detail.value);
          }, []);
          return (
            <FrVrfMultipleLineInputField
              labelContent="label"
              content={inputValue}
              onInputValueChanged={onInputValueChanged}
              memberConstraint={`[Size(${inputValue}, {min: 1, max: 5}), IsANumber(${inputValue})]`}
              constraint="IsNotEmpty(value)"
            />
          );
        },
      },
    ],
  };

  const frVrfNumberInputField: Component = {
    name: "Number Input Field",
    tagName: "<FrVrfNumberInputField>",
    samples: [
      {
        description: "Number Input Field (default)",
        Sample: () => {
          const [inputValue, setInputValue] = useState<string>("12345678");
          const onInputValueChanged = useCallback((evt: CustomEvent) => {
            setInputValue(evt.detail.value);
          }, []);
          return (
            <FrVrfNumberInputField
              labelContent="label label label"
              content={inputValue}
              onInputValueChanged={onInputValueChanged}
            />
          );
        },
      },
      {
        description: "Number Input Field (labelPosition)",
        Sample: () => {
          const [inputValue, setInputValue] = useState<string>("12345678");
          const onInputValueChanged = useCallback((evt: CustomEvent) => {
            setInputValue(evt.detail.value);
          }, []);
          return (
            <FrVrfNumberInputField
              labelContent="label label label"
              labelPosition="top"
              content={inputValue}
              onInputValueChanged={onInputValueChanged}
            />
          );
        },
      },
      {
        description: "Number Input Field (placeholder)",
        Sample: () => {
          const [inputValue, setInputValue] = useState<string>();
          const onInputValueChanged = useCallback((evt: CustomEvent) => {
            setInputValue(evt.detail.value);
          }, []);
          return (
            <FrVrfNumberInputField
              labelContent="label label label"
              placeholder="080xxxxxxxx"
              content={inputValue}
              onInputValueChanged={onInputValueChanged}
            />
          );
        },
      },
      {
        description: "Number Input Field (decimalDisplay/decimalDigit)",
        Sample: () => {
          const [inputValue, setInputValue] = useState<string>("10.22222");
          const onInputValueChanged = useCallback((evt: CustomEvent) => {
            setInputValue(evt.detail.value);
          }, []);
          return (
            <FrVrfNumberInputField
              labelContent="label label label"
              decimalDisplay={true}
              decimalDigit={5}
              content={inputValue}
              onInputValueChanged={onInputValueChanged}
            />
          );
        },
      },
      {
        description: "Number Input Field (displayBytes)",
        Sample: () => {
          const [inputValue, setInputValue] = useState<string>("12345");
          const onInputValueChanged = useCallback((evt: CustomEvent) => {
            setInputValue(evt.detail.value);
          }, []);
          return (
            <FrVrfNumberInputField
              labelContent="label label label"
              displayBytes={5}
              content={inputValue}
              onInputValueChanged={onInputValueChanged}
            />
          );
        },
      },
      {
        description: "Number Input Field (required)",
        Sample: () => {
          const [inputValue, setInputValue] = useState<string>();
          const onInputValueChanged = useCallback((evt: CustomEvent) => {
            setInputValue(evt.detail.value);
          }, []);
          return (
            <FrVrfNumberInputField
              labelContent="label label label"
              required
              content={inputValue}
              onInputValueChanged={onInputValueChanged}
            />
          );
        },
      },
      {
        description: "Number Input Field (disabled)",
        Sample: () => {
          const [inputValue, setInputValue] = useState<string>();
          const onInputValueChanged = useCallback((evt: CustomEvent) => {
            setInputValue(evt.detail.value);
          }, []);
          return (
            <FrVrfNumberInputField
              labelContent="label label label"
              disabled
              content={inputValue}
              onInputValueChanged={onInputValueChanged}
            />
          );
        },
      },
      {
        description: "Number Input Field (clearButtonVisible)",
        Sample: () => {
          const [inputValue, setInputValue] = useState<string>("12345");
          const onInputValueChanged = useCallback((evt: CustomEvent) => {
            setInputValue(evt.detail.value);
          }, []);
          return (
            <FrVrfNumberInputField
              labelContent="label label label"
              clearButtonVisible
              content={inputValue}
              onInputValueChanged={onInputValueChanged}
            />
          );
        },
      },
      {
        description: "Number Input Field (errorMessage)",
        Sample: () => {
          const [inputValue, setInputValue] = useState<string>();
          const onInputValueChanged = useCallback((evt: CustomEvent) => {
            setInputValue(evt.detail.value);
          }, []);
          return (
            <FrVrfNumberInputField
              labelContent="label label label"
              errorMessage="Please input positive number."
              content={inputValue}
              onInputValueChanged={onInputValueChanged}
            />
          );
        },
      },
      {
        description: "Number Input Field (textAlign)",
        Sample: () => {
          const [inputValue, setInputValue] = useState<string>("12345");
          const onInputValueChanged = useCallback((evt: CustomEvent) => {
            setInputValue(evt.detail.value);
          }, []);
          return (
            <FrVrfNumberInputField
              labelContent="label label label"
              textAlign="right"
              content={inputValue}
              onInputValueChanged={onInputValueChanged}
            />
          );
        },
      },
      {
        description: "Number Input Field (style)",
        Sample: () => {
          const [inputValue, setInputValue] = useState<string>("123456789");
          const onInputValueChanged = useCallback((evt: CustomEvent) => {
            setInputValue(evt.detail.value);
          }, []);
          return (
            <FrVrfNumberInputField
              style={{
                "--vrf-number-input-field-font-size": "20px",
                "--vrf-number-input-field-input-width": "400px",
                "--vrf-number-input-field-label-width": "30px",
                "--vrf-number-input-field-label-font-size": "5px",
              }}
              labelContent="label label label"
              errorMessage="Please input positive number."
              content={inputValue}
              onInputValueChanged={onInputValueChanged}
            />
          );
        },
      },
      {
        description: "Number Input Field (information)",
        Sample: () => {
          const [inputValue, setInputValue] = useState<string>();
          const onInputValueChanged = useCallback((evt: CustomEvent) => {
            setInputValue(evt.detail.value);
          }, []);
          return (
            <FrVrfNumberInputField
              labelContent="label label label"
              content={inputValue}
              onInputValueChanged={onInputValueChanged}
              information="information"
            />
          );
        },
      },
      {
        description: "Number Input Field (constraint)",
        Sample: () => {
          const [inputValue, setInputValue] = useState<string>("1234567");
          const onInputValueChanged = useCallback((evt: CustomEvent) => {
            setInputValue(evt.detail.value);
          }, []);
          return (
            <FrVrfNumberInputField
              labelContent="label label label"
              content={inputValue}
              onInputValueChanged={onInputValueChanged}
              constraint={`Size(${inputValue}, {min: 1, max: 5})`}
            />
          );
        },
      },
    ],
  };

  const frVrfSingleLineDisplayField: Component = {
    name: "Single Line Display Field",
    tagName: "<FrVrfSingleLineDisplayField>",
    samples: [
      {
        description: "Single Line Display Field (default)",
        Sample: () => {
          return (
            <FrVrfSingleLineDisplayField
              label="label label label"
              firstValue="VALUE1"
              secondValue="VALUE2"
            />
          );
        },
      },
      {
        description: "Single Line Display Field (labelPosition)",
        Sample: () => {
          return (
            <FrVrfSingleLineDisplayField
              label="label label label"
              labelPosition="top"
              firstValue="VALUE1"
              secondValue="VALUE2"
            />
          );
        },
      },
    ],
  };

  const frVrfSingleLinkDisplayField: Component = {
    name: "Single Link Display Field",
    tagName: "<FrVrfSingleLinkDisplayField>",
    samples: [
      {
        description: "Single Link Display Field (default)",
        Sample: () => {
          return (
            <FrVrfSingleLinkDisplayField
              labelContent="label label label"
              content="Link Text"
            />
          );
        },
      },
      {
        description: "Single Link Display Field (labelPosition)",
        Sample: () => {
          return (
            <FrVrfSingleLinkDisplayField
              labelContent="label label label"
              labelPosition="top"
              content="Link Text"
            />
          );
        },
      },
    ],
  };

  const frVrfTextDisplayField: Component = {
    name: "Text Display Field",
    tagName: "<FrVrfTextDisplayField>",
    samples: [
      {
        description: "Text Display Field (default)",
        Sample: () => {
          return <FrVrfTextDisplayField text="test test test test test" />;
        },
      },
      {
        description: "Text Display Field (part=content)",
        Sample: () => {
          return (
            <FrVrfTextDisplayField
              text="test test test test test"
              className="text-display-field-style-apply"
            />
          );
        },
      },
    ],
  };

  const frVrfInputDatepickerField: Component = {
    name: "Input Datepicker Field",
    tagName: "<FrVrfInputDatepickerField>",
    samples: [
      {
        description: "Input Datepicker Field (default)",
        Sample: () => {
          const [inputValue, setInputValue] = useState<string>();
          const onInputValueChanged = useCallback((evt: CustomEvent) => {
            setInputValue(evt.detail.date);
          }, []);
          return (
            <FrVrfInputDatepickerField
              labelContent="label label label"
              defaultDate={inputValue}
              onInputValueChanged={onInputValueChanged}
            />
          );
        },
      },
      {
        description: "Input Datepicker Field (labelPosition)",
        Sample: () => {
          const [inputValue, setInputValue] = useState<string>();
          const onInputValueChanged = useCallback((evt: CustomEvent) => {
            setInputValue(evt.detail.date);
          }, []);
          return (
            <FrVrfInputDatepickerField
              labelContent="label label label"
              labelPosition="top"
              defaultDate={inputValue}
              onInputValueChanged={onInputValueChanged}
            />
          );
        },
      },
      {
        description: "Input Datepicker Field (dateFormat)",
        Sample: () => {
          const [inputValue, setInputValue] = useState<string>();
          const [ym, setYm] = useState();
          return (
            <FrVrfInputDatepickerField
              labelContent="label label label"
              defaultDate={ym}
              dateFormat="ym"
              onInputValueChanged={(evt: CustomEvent) => {
                setYm(evt.detail.date);
              }}
            />
          );
        },
      },
      {
        description: "Input Datepicker Field (required)",
        Sample: () => {
          const [inputValue, setInputValue] = useState<string>();
          const onInputValueChanged = useCallback((evt: CustomEvent) => {
            setInputValue(evt.detail.date);
          }, []);
          return (
            <FrVrfInputDatepickerField
              labelContent="label label label"
              required
              defaultDate={inputValue}
              onInputValueChanged={onInputValueChanged}
            />
          );
        },
      },
      {
        description: "Input Datepicker Field (disabled)",
        Sample: () => {
          const [inputValue, setInputValue] = useState<string>();
          const onInputValueChanged = useCallback((evt: CustomEvent) => {
            setInputValue(evt.detail.date);
          }, []);
          return (
            <FrVrfInputDatepickerField
              labelContent="label label label"
              disabled
              defaultDate={inputValue}
              onInputValueChanged={onInputValueChanged}
            />
          );
        },
      },
      {
        description: "Input Datepicker Field (clearButtonVisible)",
        Sample: () => {
          const [inputValue, setInputValue] = useState<string>();
          const onInputValueChanged = useCallback((evt: CustomEvent) => {
            setInputValue(evt.detail.date);
          }, []);
          return (
            <FrVrfInputDatepickerField
              labelContent="label label label"
              clearButtonVisible
              defaultDate={inputValue}
              onInputValueChanged={onInputValueChanged}
            />
          );
        },
      },
      {
        description: "Input Datepicker Field (errorMessage)",
        Sample: () => {
          const [inputValue, setInputValue] = useState<string>();
          const [errorMsg, setErrorMsg] = useState<string>(
            "you must select one date",
          );
          const onInputValueChanged = useCallback((evt: CustomEvent) => {
            setInputValue(evt.detail.date);
          }, []);
          return (
            <FrVrfInputDatepickerField
              labelContent="label label label"
              defaultDate={inputValue}
              errorMessage={errorMsg}
              onInputValueChanged={onInputValueChanged}
            />
          );
        },
      },
      {
        description: "Input Datepicker Field (minFrom/maxTo)",
        Sample: () => {
          const [inputValue, setInputValue] = useState<string>();
          const onInputValueChanged = useCallback((evt: CustomEvent) => {
            setInputValue(evt.detail.date);
          }, []);
          return (
            <FrVrfInputDatepickerField
              labelContent="label label label"
              defaultDate={inputValue}
              minFrom="2022-09-15"
              maxTo="2022-12-31"
              onInputValueChanged={onInputValueChanged}
            />
          );
        },
      },
    ],
  };

  const frVrfInputDaterangeField: Component = {
    name: "Input Daterange Field",
    tagName: "<FrVrfInputDaterangeField>",
    samples: [
      {
        description: "Input Daterange Field (default)",
        Sample: () => {
          const [inputValue, setInputValue] = useState<string>();
          const [inputDateRange, setInputDateRange] = useState<{
            startDate: string;
            endDate: string;
          }>({ startDate: "", endDate: "" });
          const onInputValueChanged = useCallback((evt: CustomEvent) => {
            setInputValue(evt.detail.startDate);
            setInputDateRange({
              ...inputDateRange,
              startDate: evt.detail.startDate,
              endDate: evt.detail.endDate,
            });
          }, []);
          return (
            <FrVrfInputDaterangeField
              labelContent="label label label"
              inputDateRange={inputDateRange}
              onInputValueChanged={onInputValueChanged}
            />
          );
        },
      },
      {
        description: "Input Daterange Field (labelPosition)",
        Sample: () => {
          const [inputValue, setInputValue] = useState<string>();
          const [inputDateRange, setInputDateRange] = useState<{
            startDate: string;
            endDate: string;
          }>({ startDate: "", endDate: "" });
          const onInputValueChanged = useCallback((evt: CustomEvent) => {
            setInputValue(evt.detail.startDate);
            setInputDateRange({
              ...inputDateRange,
              startDate: evt.detail.startDate,
              endDate: evt.detail.endDate,
            });
          }, []);
          return (
            <FrVrfInputDaterangeField
              labelContent="label label label"
              labelPosition="top"
              inputDateRange={inputDateRange}
              onInputValueChanged={onInputValueChanged}
            />
          );
        },
      },
      {
        description: "Input Daterange Field (dateFormat)",
        Sample: () => {
          const [inputValue, setInputValue] = useState<string>();
          const [inputDateRange, setInputDateRange] = useState<{
            startDate: string;
            endDate: string;
          }>({ startDate: "", endDate: "" });
          const [ym, setYm] = useState();
          const onInputValueChanged = useCallback((evt: CustomEvent) => {
            setInputValue(evt.detail.startDate);
            setInputDateRange({
              ...inputDateRange,
              startDate: evt.detail.startDate,
              endDate: evt.detail.endDate,
            });
          }, []);
          return (
            <FrVrfInputDaterangeField
              labelContent="label label label"
              defaultStartDate={ym}
              dateFormat="ym"
              inputDateRange={inputDateRange}
              onInputValueChanged={onInputValueChanged}
            />
          );
        },
      },
      {
        description: "Input Daterange Field (required)",
        Sample: () => {
          const [inputValue, setInputValue] = useState<string>();
          const [inputDateRange, setInputDateRange] = useState<{
            startDate: string;
            endDate: string;
          }>({ startDate: "", endDate: "" });
          const [ym, setYm] = useState();
          const onInputValueChanged = useCallback((evt: CustomEvent) => {
            setInputValue(evt.detail.startDate);
            setInputDateRange({
              ...inputDateRange,
              startDate: evt.detail.startDate,
              endDate: evt.detail.endDate,
            });
          }, []);
          return (
            <FrVrfInputDaterangeField
              labelContent="label label label"
              required
              inputDateRange={inputDateRange}
              onInputValueChanged={onInputValueChanged}
            />
          );
        },
      },
      {
        description: "Input Daterange Field (disabled)",
        Sample: () => {
          const [inputValue, setInputValue] = useState<string>();
          const [inputDateRange, setInputDateRange] = useState<{
            startDate: string;
            endDate: string;
          }>({ startDate: "", endDate: "" });
          const [ym, setYm] = useState();
          const onInputValueChanged = useCallback((evt: CustomEvent) => {
            setInputValue(evt.detail.startDate);
            setInputDateRange({
              ...inputDateRange,
              startDate: evt.detail.startDate,
              endDate: evt.detail.endDate,
            });
          }, []);
          return (
            <FrVrfInputDaterangeField
              labelContent="label label label"
              disabled
              inputDateRange={inputDateRange}
              onInputValueChanged={onInputValueChanged}
            />
          );
        },
      },
      {
        description: "Input Daterange Field (clearButtonVisible)",
        Sample: () => {
          const [inputValue, setInputValue] = useState<string>();
          const [inputDateRange, setInputDateRange] = useState<{
            startDate: string;
            endDate: string;
          }>({ startDate: "", endDate: "" });
          const [ym, setYm] = useState();
          const onInputValueChanged = useCallback((evt: CustomEvent) => {
            setInputValue(evt.detail.startDate);
            setInputDateRange({
              ...inputDateRange,
              startDate: evt.detail.startDate,
              endDate: evt.detail.endDate,
            });
          }, []);
          return (
            <FrVrfInputDaterangeField
              labelContent="label label label"
              clearButtonVisible
              inputDateRange={inputDateRange}
              onInputValueChanged={onInputValueChanged}
            />
          );
        },
      },
      {
        description: "Input Daterange Field (errorMessage)",
        Sample: () => {
          const [inputValue, setInputValue] = useState<string>();
          const [inputDateRange, setInputDateRange] = useState<{
            startDate: string;
            endDate: string;
          }>({ startDate: "", endDate: "" });
          const [errorMsg, setErrorMsg] = useState<string>(
            "you must select one date",
          );
          const [ym, setYm] = useState();
          const onInputValueChanged = useCallback((evt: CustomEvent) => {
            setInputValue(evt.detail.startDate);
            setInputDateRange({
              ...inputDateRange,
              startDate: evt.detail.startDate,
              endDate: evt.detail.endDate,
            });
          }, []);
          return (
            <FrVrfInputDaterangeField
              labelContent="label label label"
              errorMessage={errorMsg}
              inputDateRange={inputDateRange}
              onInputValueChanged={onInputValueChanged}
            />
          );
        },
      },
      {
        description: "Input Daterange Field(minFrom/maxTo)",
        Sample: () => {
          const [inputValue, setInputValue] = useState<string>("2021-08-09");
          const [inputDateRange, setInputDateRange] = useState<{
            startDate: string;
            endDate: string;
          }>({ startDate: "", endDate: "" });
          const onInputValueChanged = useCallback((evt: CustomEvent) => {
            setInputValue(evt.detail.startDate);
            setInputDateRange({
              ...inputDateRange,
              startDate: evt.detail.startDate,
              endDate: evt.detail.endDate,
            });
          }, []);
          return (
            <FrVrfInputDaterangeField
              labelContent="Input Daterange Field"
              inputDateRange={inputDateRange}
              minFrom="2022-09-15"
              maxTo="2022-12-10"
              onInputValueChanged={onInputValueChanged}
            />
          );
        },
      },
      {
        description: "Input Daterange Field (pullDownDisplay)",
        Sample: () => {
          const [inputValue, setInputValue] = useState<string>();
          const [inputDateRange, setInputDateRange] = useState<{
            startDate: string;
            endDate: string;
          }>({ startDate: "", endDate: "" });
          const [ym, setYm] = useState();
          const onInputValueChanged = useCallback((evt: CustomEvent) => {
            setInputValue(evt.detail.startDate);
            setInputDateRange({
              ...inputDateRange,
              startDate: evt.detail.startDate,
              endDate: evt.detail.endDate,
            });
          }, []);
          return (
            <FrVrfInputDaterangeField
              labelContent="label label label"
              pullDownDisplay={true}
              inputDateRange={inputDateRange}
              onInputValueChanged={onInputValueChanged}
            />
          );
        },
      },
    ],
  };

  const frVrfCheckboxField: Component = {
    name: "Checkbox Field",
    tagName: "<FrVrfCheckboxField>",
    samples: [
      {
        description: "Checkbox Field (default)",
        Sample: () => {
          const onCheckedChanged = useCallback((evt: CustomEvent) => {}, []);
          const onClicked = useCallback((evt: CustomEvent) => {}, []);
          return (
            <FrVrfCheckboxField
              labelContent="label label label"
              content="CheckBox Content"
              onCheckedChanged={onCheckedChanged}
              onClicked={onClicked}
            />
          );
        },
      },
      {
        description: "Checkbox Field (labelPosition)",
        Sample: () => {
          const onCheckedChanged = useCallback((evt: CustomEvent) => {}, []);
          const onClicked = useCallback((evt: CustomEvent) => {}, []);
          return (
            <FrVrfCheckboxField
              labelContent="label label label"
              labelPosition="top"
              content="CheckBox Content"
              onCheckedChanged={onCheckedChanged}
              onClicked={onClicked}
            />
          );
        },
      },
      {
        description: "Checkbox Field (required)",
        Sample: () => {
          const onCheckedChanged = useCallback((evt: CustomEvent) => {}, []);
          const onClicked = useCallback((evt: CustomEvent) => {}, []);
          return (
            <FrVrfCheckboxField
              labelContent="label label label"
              required
              content="CheckBox Content"
              onCheckedChanged={onCheckedChanged}
              onClicked={onClicked}
            />
          );
        },
      },
      {
        description: "Checkbox Field (disabled)",
        Sample: () => {
          const onCheckedChanged = useCallback((evt: CustomEvent) => {}, []);
          const onClicked = useCallback((evt: CustomEvent) => {}, []);
          return (
            <FrVrfCheckboxField
              labelContent="label label label"
              disabled
              content="CheckBox Content"
              onCheckedChanged={onCheckedChanged}
              onClicked={onClicked}
            />
          );
        },
      },
      {
        description: "Checkbox Field (errorMessage)",
        Sample: () => {
          const onCheckedChanged = useCallback((evt: CustomEvent) => {}, []);
          const onClicked = useCallback((evt: CustomEvent) => {}, []);
          return (
            <FrVrfCheckboxField
              labelContent="label label label"
              errorMessage="Check required."
              content="CheckBox Content"
              onCheckedChanged={onCheckedChanged}
              onClicked={onClicked}
            />
          );
        },
      },
    ],
  };

  const frVrfCheckboxesField: Component = {
    name: "Checkboxes Field",
    tagName: "<FrVrfCheckboxesField>",
    samples: [
      {
        description: "Checkboxes Field (default)",
        Sample: () => {
          const onCheckedChanged = useCallback((evt: CustomEvent) => {}, []);

          const options = [
            { label: "CHECKBOX1", value: "1", disabled: false },
            { label: "CHECKBOX2", value: "2", disabled: false },
          ];
          const checkList = ["1"];
          return (
            <FrVrfCheckboxesField
              labelContent="label label label"
              options={options}
              checkList={checkList}
              onCheckboxesChanged={onCheckedChanged}
            />
          );
        },
      },
      {
        description: "Checkboxes Field (labelPosition)",
        Sample: () => {
          const options = [
            { label: "CHECKBOX1", value: "1", disabled: false },
            { label: "CHECKBOX2", value: "2", disabled: false },
          ];
          const checkList = ["1"];
          return (
            <FrVrfCheckboxesField
              labelContent="label label label"
              labelPosition="top"
              options={options}
              checkList={checkList}
            />
          );
        },
      },
      {
        description: "Checkboxes Field (required)",
        Sample: () => {
          const options = [
            { label: "CHECKBOX1", value: "1", disabled: false },
            { label: "CHECKBOX2", value: "2", disabled: false },
          ];
          const checkList = ["1"];
          return (
            <FrVrfCheckboxesField
              labelContent="label label label"
              required
              options={options}
              checkList={checkList}
            />
          );
        },
      },
      {
        description: "Checkboxes Field (disabled)",
        Sample: () => {
          const options = [
            { label: "CHECKBOX1", value: "1", disabled: false },
            { label: "CHECKBOX2", value: "2", disabled: false },
          ];
          const checkList = ["1"];
          return (
            <FrVrfCheckboxesField
              labelContent="label label label"
              disabled
              options={options}
              checkList={checkList}
            />
          );
        },
      },
      {
        description: "Checkboxes Field (errorMessage)",
        Sample: () => {
          const options = [
            { label: "CHECKBOX1", value: "1", disabled: false },
            { label: "CHECKBOX2", value: "2", disabled: false },
          ];
          const checkList = ["1"];
          return (
            <FrVrfCheckboxesField
              labelContent="label label label"
              errorMessage="All checkbox required."
              options={options}
              checkList={checkList}
            />
          );
        },
      },
    ],
  };

  const frVrfDropdownField: Component = {
    name: "Dropdown Field",
    tagName: "<FrVrfDropdownField>",
    samples: [
      {
        description: "Dropdown Field (default)",
        Sample: () => {
          const [selectValue, setSelectValue] = useState<string>("");
          const valueChangeHandle = useCallback((evt: CustomEvent) => {
            setSelectValue(evt.detail.value);
          }, []);
          return (
            <FrVrfDropdownField
              labelContent="label label label"
              selectedValue={selectValue}
              onValueChanged={valueChangeHandle}
              items={WebComponentsConstants.dropdownItems}
            />
          );
        },
      },
      {
        description: "Dropdown Field (labelPosition)",
        Sample: () => {
          const [selectValue, setSelectValue] = useState<string>("");
          const valueChangeHandle = useCallback((evt: CustomEvent) => {
            setSelectValue(evt.detail.value);
          }, []);
          return (
            <FrVrfDropdownField
              labelContent="label label label"
              labelPosition="top"
              selectedValue={selectValue}
              onValueChanged={valueChangeHandle}
              items={WebComponentsConstants.dropdownItems}
            />
          );
        },
      },
      {
        description: "Dropdown Field (placeholder)",
        Sample: () => {
          const [selectValue, setSelectValue] = useState<string>("");
          const valueChangeHandle = useCallback((evt: CustomEvent) => {
            setSelectValue(evt.detail.value);
          }, []);
          return (
            <FrVrfDropdownField
              labelContent="label label label"
              placeholder="Placeholder."
              includeDefaultValue={false}
              selectedValue={selectValue}
              onValueChanged={valueChangeHandle}
              items={WebComponentsConstants.dropdownItems}
            />
          );
        },
      },
      {
        description: "Dropdown Field (required)",
        Sample: () => {
          const [selectValue, setSelectValue] = useState<string>("");
          const valueChangeHandle = useCallback((evt: CustomEvent) => {
            setSelectValue(evt.detail.value);
          }, []);
          return (
            <FrVrfDropdownField
              labelContent="label label label"
              required
              selectedValue={selectValue}
              onValueChanged={valueChangeHandle}
              items={WebComponentsConstants.dropdownItems}
            />
          );
        },
      },
      {
        description: "Dropdown Field (disabled)",
        Sample: () => {
          const [selectValue, setSelectValue] = useState<string>("");
          const valueChangeHandle = useCallback((evt: CustomEvent) => {
            setSelectValue(evt.detail.value);
          }, []);
          return (
            <FrVrfDropdownField
              labelContent="label label label"
              disabled
              selectedValue={selectValue}
              onValueChanged={valueChangeHandle}
              items={WebComponentsConstants.dropdownItems}
            />
          );
        },
      },
      {
        description: "Dropdown Field (errorMessage)",
        Sample: () => {
          const [selectValue, setSelectValue] = useState<string>("");
          const valueChangeHandle = useCallback((evt: CustomEvent) => {
            setSelectValue(evt.detail.value);
          }, []);
          return (
            <FrVrfDropdownField
              labelContent="label label label"
              errorMessage="Please select the value."
              selectedValue={selectValue}
              onValueChanged={valueChangeHandle}
              items={WebComponentsConstants.dropdownItems}
            />
          );
        },
      },
      {
        description: "Dropdown Field (style)",
        Sample: () => {
          const [selectValue, setSelectValue] = useState<string>("");
          const valueChangeHandle = useCallback((evt: CustomEvent) => {
            setSelectValue(evt.detail.value);
          }, []);
          return (
            <FrVrfDropdownField
              labelContent="label label label"
              style={{
                "--vrf-dropdown-field-label-width": "max-content",
                "--vrf-dropdown-field-label-font-size": "5px",
                "--vrf-dropdown-field-font-size": "20px",
                "--vrf-dropdown-field-select-width": "400px",
                "--vrf-dropdown-field-overlay-padding-top-and-bottom": "1.5px",
                "--vrf-dropdown-field-overlay-min-height": "10px",
                "--vrf-dropdown-field-overlay-font-size": "10px",
              }}
              selectedValue={selectValue}
              onValueChanged={valueChangeHandle}
              items={WebComponentsConstants.dropdownItems}
            />
          );
        },
      },
      {
        description: "Dropdown Field (information)",
        Sample: () => {
          const [selectValue, setSelectValue] = useState<string>("");
          const valueChangeHandle = useCallback((evt: CustomEvent) => {
            setSelectValue(evt.detail.value);
          }, []);
          return (
            <FrVrfDropdownField
              labelContent="label label label"
              style={{
                "--vrf-dropdown-field-label-width": "max-content",
                "--vrf-dropdown-field-label-font-size": "5px",
                "--vrf-dropdown-field-font-size": "20px",
                "--vrf-dropdown-field-select-width": "400px",
                "--vrf-dropdown-field-overlay-padding-top-and-bottom": "1.5px",
                "--vrf-dropdown-field-overlay-min-height": "10px",
                "--vrf-dropdown-field-overlay-font-size": "10px",
              }}
              selectedValue={selectValue}
              onValueChanged={valueChangeHandle}
              items={WebComponentsConstants.dropdownItems}
              information="information"
            />
          );
        },
      },
      {
        description: "Dropdown Field (constraint)",
        Sample: () => {
          const [selectValue, setSelectValue] = useState<string>("");
          const valueChangeHandle = useCallback((evt: CustomEvent) => {
            setSelectValue(evt.detail.value);
          }, []);
          return (
            <FrVrfDropdownField
              labelContent="label label label"
              selectedValue={selectValue}
              onValueChanged={valueChangeHandle}
              items={WebComponentsConstants.dropdownItems}
              constraint={`IsNotBlank(${selectValue})`}
              information="information"
            />
          );
        },
      },
    ],
  };

  const frVrfRadioButtonField: Component = {
    name: "Radio Button Field",
    tagName: "<FrVrfRadioButtonField>",
    samples: [
      {
        description: "Radio Button Field (default)",
        Sample: () => {
          const [selectorRadioValue, setSelectorRadioValue] =
            useState<string>("A1");
          const valueChangeHandle = useCallback((evt: CustomEvent) => {
            setSelectorRadioValue(evt.detail.value);
          }, []);
          return (
            <FrVrfRadioButtonField
              labelContent="label label label"
              optionItems={WebComponentsConstants.radioButtonItems}
              checked={selectorRadioValue}
              onRadioCheckedChanged={valueChangeHandle}
            />
          );
        },
      },
      {
        description: "Radio Button Field (labelPosition)",
        Sample: () => {
          const [selectorRadioValue, setSelectorRadioValue] =
            useState<string>("A1");
          const valueChangeHandle = useCallback((evt: CustomEvent) => {
            setSelectorRadioValue(evt.detail.value);
          }, []);
          return (
            <FrVrfRadioButtonField
              labelContent="label label label"
              labelPosition="top"
              optionItems={WebComponentsConstants.radioButtonItems}
              checked={selectorRadioValue}
              onRadioCheckedChanged={valueChangeHandle}
            />
          );
        },
      },
      {
        description: "Radio Button Field (required)",
        Sample: () => {
          const [selectorRadioValue, setSelectorRadioValue] =
            useState<string>("A1");
          const valueChangeHandle = useCallback((evt: CustomEvent) => {
            setSelectorRadioValue(evt.detail.value);
          }, []);
          return (
            <FrVrfRadioButtonField
              labelContent="label label label"
              required
              optionItems={WebComponentsConstants.radioButtonItems}
              checked={selectorRadioValue}
              onRadioCheckedChanged={valueChangeHandle}
            />
          );
        },
      },
      {
        description: "Radio Button Field (disabled)",
        Sample: () => {
          const [selectorRadioValue, setSelectorRadioValue] =
            useState<string>("A1");
          const valueChangeHandle = useCallback((evt: CustomEvent) => {
            setSelectorRadioValue(evt.detail.value);
          }, []);
          return (
            <FrVrfRadioButtonField
              labelContent="label label label"
              disabled
              optionItems={WebComponentsConstants.radioButtonItems}
              checked={selectorRadioValue}
              onRadioCheckedChanged={valueChangeHandle}
            />
          );
        },
      },
      {
        description: "Radio Button Field (errorMessage)",
        Sample: () => {
          const [selectorRadioValue, setSelectorRadioValue] =
            useState<string>("A1");
          const valueChangeHandle = useCallback((evt: CustomEvent) => {
            setSelectorRadioValue(evt.detail.value);
          }, []);
          return (
            <FrVrfRadioButtonField
              labelContent="label label label"
              errorMessage="you must select at least one item"
              optionItems={WebComponentsConstants.radioButtonItems}
              checked={selectorRadioValue}
              onRadioCheckedChanged={valueChangeHandle}
            />
          );
        },
      },
    ],
  };

  const frVrfSelectorInputField: Component = {
    name: "Selector Input Field",
    tagName: "<FrVrfSelectorInputField>",
    samples: [
      {
        description: "Selector Input Field (default)",
        Sample: () => {
          const [selectorInputValue, setSelectorInputValue] =
            useState<string>("");
          const valueChangeHandle = useCallback((evt: CustomEvent) => {
            setSelectorInputValue(evt.detail.value);
          }, []);
          return (
            <FrVrfSelectorInputField
              labelContent="label label label"
              content={selectorInputValue}
              onSelectorValueChanged={valueChangeHandle}
              items={WebComponentsConstants.dropdownItems}
            />
          );
        },
      },
      {
        description: "Selector Input Field (labelPosition)",
        Sample: () => {
          const [selectorInputValue, setSelectorInputValue] =
            useState<string>("");
          const valueChangeHandle = useCallback((evt: CustomEvent) => {
            setSelectorInputValue(evt.detail.value);
          }, []);
          return (
            <FrVrfSelectorInputField
              labelContent="label label label"
              labelPosition="top"
              content={selectorInputValue}
              onSelectorValueChanged={valueChangeHandle}
              items={WebComponentsConstants.dropdownItems}
            />
          );
        },
      },
      {
        description: "Selector Input Field (placeholder)",
        Sample: () => {
          const [selectorInputValue, setSelectorInputValue] =
            useState<string>("");
          const valueChangeHandle = useCallback((evt: CustomEvent) => {
            setSelectorInputValue(evt.detail.value);
          }, []);
          return (
            <FrVrfSelectorInputField
              labelContent="label label label"
              placeholder="JPY"
              content={selectorInputValue}
              onSelectorValueChanged={valueChangeHandle}
              items={WebComponentsConstants.dropdownItems}
            />
          );
        },
      },
      {
        description: "Selector Input Field (dropdownPlaceholder)",
        Sample: () => {
          const [selectorInputValue, setSelectorInputValue] =
            useState<string>("");
          const valueChangeHandle = useCallback((evt: CustomEvent) => {
            setSelectorInputValue(evt.detail.value);
          }, []);
          return (
            <FrVrfSelectorInputField
              labelContent="label label label"
              dropdownPlaceholder="Japanese Yen"
              content={selectorInputValue}
              onSelectorValueChanged={valueChangeHandle}
              items={WebComponentsConstants.dropdownItems}
            />
          );
        },
      },
      {
        description: "Selector Input Field (required)",
        Sample: () => {
          const [selectorInputValue, setSelectorInputValue] =
            useState<string>("");
          const valueChangeHandle = useCallback((evt: CustomEvent) => {
            setSelectorInputValue(evt.detail.value);
          }, []);
          return (
            <FrVrfSelectorInputField
              labelContent="label label label"
              required
              content={selectorInputValue}
              onSelectorValueChanged={valueChangeHandle}
              items={WebComponentsConstants.dropdownItems}
            />
          );
        },
      },
      {
        description: "Selector Input Field (disabled)",
        Sample: () => {
          const [selectorInputValue, setSelectorInputValue] =
            useState<string>("");
          const valueChangeHandle = useCallback((evt: CustomEvent) => {
            setSelectorInputValue(evt.detail.value);
          }, []);
          return (
            <FrVrfSelectorInputField
              labelContent="label label label"
              disabled
              content={selectorInputValue}
              onSelectorValueChanged={valueChangeHandle}
              items={WebComponentsConstants.dropdownItems}
            />
          );
        },
      },
      {
        description: "Selector Input Field (mismatchWord)",
        Sample: () => {
          const [selectorInputValue, setSelectorInputValue] =
            useState<string>("TEST");
          const valueChangeHandle = useCallback((evt: CustomEvent) => {
            setSelectorInputValue(evt.detail.value);
          }, []);
          return (
            <FrVrfSelectorInputField
              labelContent="label label label"
              mismatchWord="No Matched"
              content={selectorInputValue}
              onSelectorValueChanged={valueChangeHandle}
              items={WebComponentsConstants.dropdownItems}
            />
          );
        },
      },
      {
        description: "Selector Input Field (inputWidth/dropdownWidth)",
        Sample: () => {
          const [selectorInputValue, setSelectorInputValue] =
            useState<string>("");
          const valueChangeHandle = useCallback((evt: CustomEvent) => {
            setSelectorInputValue(evt.detail.value);
          }, []);
          return (
            <FrVrfSelectorInputField
              labelContent="label label label"
              style={{
                "--vrf-selector-input-field-label-width": "max-content",
                "--vrf-selector-input-field-label-font-size": "5px",
                "--vrf-selector-input-field-select-width": "300px",
                "--vrf-selector-input-field-input-width": "150px",
                "--vrf-selector-input-field-font-size": "20px",
                "--vrf-selector-input-field-dropdown-overlay-padding-top-and-bottom":
                  "1.5px",
                "--vrf-selector-input-field-dropdown-overlay-min-height":
                  "10px",
                "--vrf-selector-input-field-dropdown-overlay-font-size": "10px",
              }}
              content={selectorInputValue}
              errorMessage="error message"
              onSelectorValueChanged={valueChangeHandle}
              items={WebComponentsConstants.dropdownItems}
            />
          );
        },
      },
    ],
  };

  const frVrfCodeInputSearchField: Component = {
    name: "Code Input Search Field",
    tagName: "<FrVrfCodeInputSearchField>",
    samples: [
      {
        description: "Code Input Search Field (default)",
        Sample: () => {
          let searchContent = "";
          const [inputValue, setInputValue] = useState<string>("");
          const onInputValueChanged = useCallback((evt: CustomEvent) => {
            setInputValue(evt.detail.value);
          }, []);
          const onButtonClicked = useCallback((evt: CustomEvent) => {}, []);
          return (
            <FrVrfCodeInputSearchField
              labelContent="label label label"
              inputContent={inputValue}
              searchContent={searchContent}
              onInputValueChanged={onInputValueChanged}
              onButtonClicked={onButtonClicked}
            />
          );
        },
      },
      {
        description: "Code Input Search Field (labelPosition)",
        Sample: () => {
          let searchContent = "";
          const [inputValue, setInputValue] = useState<string>("");
          const onInputValueChanged = useCallback((evt: CustomEvent) => {
            setInputValue(evt.detail.value);
          }, []);
          const onButtonClicked = useCallback((evt: CustomEvent) => {}, []);
          return (
            <FrVrfCodeInputSearchField
              labelContent="label label label"
              labelPosition="top"
              inputContent={inputValue}
              searchContent={searchContent}
              onInputValueChanged={onInputValueChanged}
              onButtonClicked={onButtonClicked}
            />
          );
        },
      },
      {
        description: "Code Input Search Field (placeholder)",
        Sample: () => {
          let searchContent = "";
          const [inputValue, setInputValue] = useState<string>("");
          const onInputValueChanged = useCallback((evt: CustomEvent) => {
            setInputValue(evt.detail.value);
          }, []);
          const onButtonClicked = useCallback((evt: CustomEvent) => {}, []);
          return (
            <FrVrfCodeInputSearchField
              labelContent="label label label"
              placeholder="JPY"
              inputContent={inputValue}
              searchContent={searchContent}
              onInputValueChanged={onInputValueChanged}
              onButtonClicked={onButtonClicked}
            />
          );
        },
      },
      {
        description: "Code Input Search Field (searchPlaceholder)",
        Sample: () => {
          let searchContent = "";
          const [inputValue, setInputValue] = useState<string>("");
          const onInputValueChanged = useCallback((evt: CustomEvent) => {
            setInputValue(evt.detail.value);
          }, []);
          const onButtonClicked = useCallback((evt: CustomEvent) => {}, []);
          return (
            <FrVrfCodeInputSearchField
              labelContent="label label label"
              searchPlaceholder="Japanese Yen"
              inputContent={inputValue}
              searchContent={searchContent}
              onInputValueChanged={onInputValueChanged}
              onButtonClicked={onButtonClicked}
            />
          );
        },
      },
      {
        description: "Code Input Search Field (required)",
        Sample: () => {
          let searchContent = "";
          const [inputValue, setInputValue] = useState<string>("");
          const onInputValueChanged = useCallback((evt: CustomEvent) => {
            setInputValue(evt.detail.value);
          }, []);
          const onButtonClicked = useCallback((evt: CustomEvent) => {}, []);
          return (
            <FrVrfCodeInputSearchField
              labelContent="label label label"
              required
              inputContent={inputValue}
              searchContent={searchContent}
              onInputValueChanged={onInputValueChanged}
              onButtonClicked={onButtonClicked}
            />
          );
        },
      },
      {
        description: "Code Input Search Field (disabled)",
        Sample: () => {
          let searchContent = "";
          const [inputValue, setInputValue] = useState<string>("");
          const onInputValueChanged = useCallback((evt: CustomEvent) => {
            setInputValue(evt.detail.value);
          }, []);
          const onButtonClicked = useCallback((evt: CustomEvent) => {}, []);
          return (
            <FrVrfCodeInputSearchField
              labelContent="label label label"
              disabled
              inputContent={inputValue}
              searchContent={searchContent}
              onInputValueChanged={onInputValueChanged}
              onButtonClicked={onButtonClicked}
            />
          );
        },
      },
      {
        description: "Code Input Search Field (errorMessage)",
        Sample: () => {
          let searchContent = "";
          const [inputValue, setInputValue] = useState<string>("");
          const onInputValueChanged = useCallback((evt: CustomEvent) => {
            setInputValue(evt.detail.value);
          }, []);
          const onButtonClicked = useCallback((evt: CustomEvent) => {}, []);
          return (
            <FrVrfCodeInputSearchField
              labelContent="label label label"
              errorMessage="Input required."
              inputContent={inputValue}
              searchContent={searchContent}
              onInputValueChanged={onInputValueChanged}
              onButtonClicked={onButtonClicked}
            />
          );
        },
      },
      {
        description: "Code Input Search Field (style)",
        Sample: () => {
          let searchContent = "test test";
          const [inputValue, setInputValue] = useState<string>("");
          const onInputValueChanged = useCallback((evt: CustomEvent) => {
            setInputValue(evt.detail.value);
          }, []);
          const onButtonClicked = useCallback((evt: CustomEvent) => {}, []);
          return (
            <FrVrfCodeInputSearchField
              style={{
                "--vrf-code-input-search-field-input-width": "250px",
                "--vrf-code-input-search-field-search-width": "400px",
                "--vrf-code-input-search-field-label-width": "100px",
                "--vrf-code-input-search-field-font-size": "25px",
              }}
              labelContent="label label label"
              errorMessage="Input required."
              inputContent={inputValue}
              searchContent={searchContent}
              onInputValueChanged={onInputValueChanged}
              onButtonClicked={onButtonClicked}
            />
          );
        },
      },
      {
        description: "Code Input Search Field (information)",
        Sample: () => {
          let searchContent = "test test";
          const [inputValue, setInputValue] = useState<string>("");
          const onInputValueChanged = useCallback((evt: CustomEvent) => {
            setInputValue(evt.detail.value);
          }, []);
          const onButtonClicked = useCallback((evt: CustomEvent) => {}, []);
          return (
            <FrVrfCodeInputSearchField
              labelContent="label label label"
              errorMessage="Input required."
              inputContent={inputValue}
              searchContent={searchContent}
              onInputValueChanged={onInputValueChanged}
              onButtonClicked={onButtonClicked}
              information="information"
            />
          );
        },
      },
      {
        description: "Code Input Search Field (constraint)",
        Sample: () => {
          let searchContent = "test test";
          const [inputValue, setInputValue] = useState<string>("");
          const onInputValueChanged = useCallback((evt: CustomEvent) => {
            setInputValue(evt.detail.value);
          }, []);
          const onButtonClicked = useCallback((evt: CustomEvent) => {}, []);
          return (
            <FrVrfCodeInputSearchField
              labelContent="label label label"
              inputContent={inputValue}
              searchContent={searchContent}
              onInputValueChanged={onInputValueChanged}
              onButtonClicked={onButtonClicked}
              information="information"
              constraint="IsNotBlank(value)"
            />
          );
        },
      },
    ],
  };

  const frVrfMultipleSelectorField: Component = {
    name: "Multiple Selector Field",
    tagName: "<FrVrfMultipleSelectorField>",
    samples: [
      {
        description: "Multiple Selector Field (default)",
        Sample: () => {
          const [displayItems, setDisplayItems] = useState<string[]>([""]);
          const itemsChangeHandle = useCallback((evt: CustomEvent) => {
            setDisplayItems(evt.detail.value);
          }, []);
          return (
            <FrVrfMultipleSelectorField
              label="label label label"
              selectItems={WebComponentsConstants.multipleSelectItems}
              displayedItems={displayItems}
              onItemsChanged={itemsChangeHandle}
            />
          );
        },
      },
      {
        description: "Multiple Selector Field (labelPosition)",
        Sample: () => {
          const [displayItems, setDisplayItems] = useState<string[]>([""]);
          const itemsChangeHandle = useCallback((evt: CustomEvent) => {
            setDisplayItems(evt.detail.value);
          }, []);
          return (
            <FrVrfMultipleSelectorField
              label="label label label"
              labelPosition="top"
              selectItems={WebComponentsConstants.multipleSelectItems}
              displayedItems={displayItems}
              onItemsChanged={itemsChangeHandle}
            />
          );
        },
      },
      {
        description: "Multiple Selector Field (showNum)",
        Sample: () => {
          const [displayItems, setDisplayItems] = useState<string[]>([""]);
          const itemsChangeHandle = useCallback((evt: CustomEvent) => {
            setDisplayItems(evt.detail.value);
          }, []);
          return (
            <FrVrfMultipleSelectorField
              label="label label label"
              showNum={10}
              selectItems={WebComponentsConstants.multipleSelectItems}
              displayedItems={displayItems}
              onItemsChanged={itemsChangeHandle}
            />
          );
        },
      },
      {
        description: "Multiple Selector Field (required)",
        Sample: () => {
          const [displayItems, setDisplayItems] = useState<string[]>([""]);
          const itemsChangeHandle = useCallback((evt: CustomEvent) => {
            setDisplayItems(evt.detail.value);
          }, []);
          return (
            <FrVrfMultipleSelectorField
              label="label label label"
              required
              selectItems={WebComponentsConstants.multipleSelectItems}
              displayedItems={displayItems}
              onItemsChanged={itemsChangeHandle}
            />
          );
        },
      },
      {
        description: "Multiple Selector Field (disabled)",
        Sample: () => {
          const [displayItems, setDisplayItems] = useState<string[]>([""]);
          const itemsChangeHandle = useCallback((evt: CustomEvent) => {
            setDisplayItems(evt.detail.value);
          }, []);
          return (
            <FrVrfMultipleSelectorField
              label="label label label"
              disabled
              selectItems={WebComponentsConstants.multipleSelectItems}
              displayedItems={displayItems}
              onItemsChanged={itemsChangeHandle}
            />
          );
        },
      },
      {
        description: "Multiple Selector Field (errorMessage)",
        Sample: () => {
          const [displayItems, setDisplayItems] = useState<string[]>([""]);
          const itemsChangeHandle = useCallback((evt: CustomEvent) => {
            setDisplayItems(evt.detail.value);
          }, []);
          return (
            <FrVrfMultipleSelectorField
              label="label label label"
              errorMessage="Please select at least one."
              selectItems={WebComponentsConstants.multipleSelectItems}
              displayedItems={displayItems}
              onItemsChanged={itemsChangeHandle}
            />
          );
        },
      },
      {
        description: "Multiple Selector Field (style)",
        Sample: () => {
          const [displayItems, setDisplayItems] = useState<string[]>([""]);
          const itemsChangeHandle = useCallback((evt: CustomEvent) => {
            setDisplayItems(evt.detail.value);
          }, []);
          return (
            <FrVrfMultipleSelectorField
              style={{
                "--vrf-multiple-selector-field-label-width": "30px",
                "--vrf-multiple-selector-field-label-font-size": "5px",
                "--vrf-multiple-selector-field-font-size": "20px",
                "--vrf-multiple-selector-field-selected-values-max-width":
                  "100px",
                "--vrf-multiple-selector-content-font-size": "10px",
                "--vrf-multiple-selector-content-items-max-height": "100px",
                "--vrf-multiple-selector-content-items-max-width": "200px",
                "--vrf-multiple-selector-content-item-min-width": "50px",
                "--vrf-multiple-selector-content-item-min-height": "10px",
                "--vrf-multiple-selector-content-item-padding-left-and-right":
                  "4px",
                "--vrf-multiple-selector-content-item-padding-top-and-bottom":
                  "2px",
                "--vrf-multiple-selector-content-item-margin-left-and-right":
                  "2px",
                "--vrf-multiple-selector-content-item-margin-top-and-bottom":
                  "1px",
                "--vrf-multiple-selector-content-item-border-radius": "5px",
              }}
              label="label label label"
              showNum={10}
              errorMessage="Please select at least one."
              selectItems={WebComponentsConstants.multipleSelectItems}
              displayedItems={displayItems}
              onItemsChanged={itemsChangeHandle}
            />
          );
        },
      },
      {
        description: "Multiple Selector Field (information)",
        Sample: () => {
          const [displayItems, setDisplayItems] = useState<string[]>([""]);
          const itemsChangeHandle = useCallback((evt: CustomEvent) => {
            setDisplayItems(evt.detail.value);
          }, []);
          return (
            <FrVrfMultipleSelectorField
              label="label label label"
              showNum={10}
              selectItems={WebComponentsConstants.multipleSelectItems}
              displayedItems={displayItems}
              onItemsChanged={itemsChangeHandle}
              information="information"
            />
          );
        },
      },
      {
        description: "Multiple Selector Field (constraint)",
        Sample: () => {
          const [displayItems, setDisplayItems] = useState<string[]>([""]);
          const itemsChangeHandle = useCallback((evt: CustomEvent) => {
            setDisplayItems(evt.detail.value);
          }, []);
          return (
            <FrVrfMultipleSelectorField
              label="label label label"
              selectItems={WebComponentsConstants.multipleSelectItems}
              displayedItems={displayItems}
              onItemsChanged={itemsChangeHandle}
              constraint="IsNotEmpty(value)"
              information="information"
            />
          );
        },
      },
    ],
  };

  const frVrfButton: Component = {
    name: "Button",
    tagName: "<FrVrfButton>",
    samples: [
      {
        description: "Button (default)",
        Sample: () => {
          const [selectorInputValue, setSelectorInputValue] = useState<any>();
          const onClicked = useCallback((evt: CustomEvent) => {}, []);
          return <FrVrfButton content="CLICK" onClicked={onClicked} />;
        },
      },
      {
        description: "Button (size)",
        Sample: () => {
          const [selectorInputValue, setSelectorInputValue] = useState<any>();
          const onClicked = useCallback((evt: CustomEvent) => {}, []);
          return (
            <>
              <FrVrfButton content="CLICK" size="small" onClicked={onClicked} />
              <FrVrfButton
                content="CLICK"
                size="medium"
                onClicked={onClicked}
              />
              <FrVrfButton content="CLICK" size="large" onClicked={onClicked} />
            </>
          );
        },
      },
      {
        description: "Button (theme)",
        Sample: () => {
          const [selectorInputValue, setSelectorInputValue] = useState<any>();
          const onClicked = useCallback((evt: CustomEvent) => {}, []);
          return (
            <>
              <FrVrfButton
                content="CLICK"
                theme="primary"
                onClicked={onClicked}
              />
              <FrVrfButton
                content="CLICK"
                theme="secondary"
                onClicked={onClicked}
              />
              <FrVrfButton
                content="CLICK"
                theme="caution"
                onClicked={onClicked}
              />
              <FrVrfButton
                content="CLICK"
                theme="cautionSecondary"
                onClicked={onClicked}
              />
            </>
          );
        },
      },
      {
        description: "Button (icon)",
        Sample: () => {
          const [selectorInputValue, setSelectorInputValue] = useState<any>();
          const onClicked = useCallback((evt: CustomEvent) => {}, []);
          return (
            <>
              <FrVrfButton
                content="CLICK"
                icon="frIcon:search"
                onClicked={onClicked}
              />
              <FrVrfButton
                content="CLICK"
                icon="frIcon:edit"
                onClicked={onClicked}
              />
            </>
          );
        },
      },
      {
        description: "Button (disabled)",
        Sample: () => {
          const [selectorInputValue, setSelectorInputValue] = useState<any>();
          const onClicked = useCallback((evt: CustomEvent) => {}, []);
          return (
            <>
              <FrVrfButton content="CLICK" disabled onClicked={onClicked} />
            </>
          );
        },
      },
      {
        description: "Button (style)",
        Sample: () => {
          const [selectorInputValue, setSelectorInputValue] = useState<any>();
          const onClicked = useCallback((evt: CustomEvent) => {}, []);
          return (
            <>
              <FrVrfButton
                style={{
                  "--vrf-button-medium-height": "80px",
                  "--vrf-button-medium-min-width": "300px",
                  "--vrf-button-medium-margin": "0 24px 0 0 ",
                }}
                content="CLICK"
                size="medium"
                onClicked={onClicked}
              />
              <FrVrfButton
                style={{
                  "--vrf-button-height": "200px",
                  "--vrf-button-min-width": "200px",
                  "--vrf-button-icon-size": "40px",
                  "--vrf-button-font-size": "25px",
                }}
                content="CLICK"
                icon="frIcon:edit"
                onClicked={onClicked}
              />
            </>
          );
        },
      },
    ],
  };

  const frVrfIcon: Component = {
    name: "Icon",
    tagName: "<FrVrfIcon>",
    samples: [
      {
        description: "icon (default)",
        Sample: () => {
          const [search, setSearch] = useState("");

          return (
            <>
              <div className="icon-grid">
                {WebComponentsConstants.iconList
                  .filter((i) => new RegExp(search).test(i))
                  .map((icon) => (
                    <div key={icon}>
                      <FrVrfIcon icon={icon as any} />
                      <div>{icon}</div>
                    </div>
                  ))}
              </div>
            </>
          );
        },
      },
      {
        description: "icon (alternateIcon)",
        Sample: () => {
          const onClicked = useCallback((evt: CustomEvent) => {}, []);
          return (
            <FrVrfIcon
              onIconClicked={onClicked}
              icon="frIcon:square"
              alternateIcon="frIcon:check-square"
            />
          );
        },
      },
      {
        description: "icon (disabled)",
        Sample: () => {
          const onClicked = useCallback((evt: CustomEvent) => {}, []);
          return (
            <FrVrfIcon
              disabled={true}
              onIconClicked={onClicked}
              icon="frIcon:calendar"
            />
          );
        },
      },
      {
        description: "icon (style)",
        Sample: () => {
          const onClicked = useCallback((evt: CustomEvent) => {}, []);
          return (
            <FrVrfIcon
              icon="frIcon:calendar"
              style={{
                "--vrf-icon-size": "50px",
                "--vrf-icon-color": "red",
              }}
              onIconClicked={onClicked}
            />
          );
        },
      },
    ],
  };

  const frVrfSearchResultSummary: Component = {
    name: "Search Result Summary",
    tagName: "<FrVrfSearchResultSummary>",
    samples: [
      {
        description: "Search Result Summary (default)",
        Sample: () => {
          const onSearchButtonClicked = () => {
            alert("onSearchButtonClicked");
          };
          return (
            <FrVrfSearchResultSummary
              searchConditionItems={WebComponentsConstants.searchConditions}
              searchConditions={WebComponentsConstants.request.params}
              onSearchButtonClicked={onSearchButtonClicked}
            />
          );
        },
      },
      {
        description: "Search Result Summary (showSearchButton)",
        Sample: () => {
          const onSearchButtonClicked = () => {
            alert("onSearchButtonClicked");
          };
          return (
            <FrVrfSearchResultSummary
              searchConditionItems={WebComponentsConstants.searchConditions}
              searchConditions={WebComponentsConstants.request.params}
              showSearchButton={false}
              onSearchButtonClicked={onSearchButtonClicked}
            />
          );
        },
      },
      {
        description:
          "Search Result Summary (searchResultCountLabel/searchResultItemCount)",
        Sample: () => {
          const onSearchButtonClicked = () => {
            alert("onSearchButtonClicked");
          };
          return (
            <FrVrfSearchResultSummary
              searchConditionItems={WebComponentsConstants.searchConditions}
              searchConditions={WebComponentsConstants.request.params}
              searchResultCountLabel="RESULT COUNT IS "
              searchResultItemCount={2000}
              onSearchButtonClicked={onSearchButtonClicked}
            />
          );
        },
      },
      {
        description: "Search Result Summary (buttonName)",
        Sample: () => {
          const onSearchButtonClicked = () => {
            alert("onSearchButtonClicked");
          };
          return (
            <FrVrfSearchResultSummary
              searchConditionItems={WebComponentsConstants.searchConditions}
              searchConditions={WebComponentsConstants.request.params}
              buttonName="BACK"
              onSearchButtonClicked={onSearchButtonClicked}
            />
          );
        },
      },
    ],
  };

  const frVrfTab: Component = {
    name: "Tab",
    tagName: "<FrVrfTab>",
    samples: [
      {
        description: "Tab (Content outside)",
        Sample: () => {
          const [selectValue, setSelectValue] = useState<string>("1");
          const valueChangeHandle = (evt: CustomEvent) => {
            setSelectValue(evt.detail.value);
          };
          return (
            <>
              <FrVrfTab
                selectValue={selectValue}
                onSelectedTabChanged={valueChangeHandle}
                tabList={WebComponentsConstants.tabItems}
              />
              <div>Tab content1</div>
              <div>Tab content2</div>
              <div>Tab content3</div>
              <div>Tab content4</div>
              <div>Tab content5</div>
            </>
          );
        },
      },
      {
        description: "Tab(Content inside)",
        Sample: () => {
          const [selectValue, setSelectValue] = useState<string>("1");
          const valueChangeHandle = (evt: CustomEvent) => {
            setSelectValue(evt.detail.value);
          };
          return (
            <FrVrfTab
              selectValue={selectValue}
              onSelectedTabChanged={valueChangeHandle}
              tabList={WebComponentsConstants.tabItems}
            >
              <div>Tab content1</div>
              <div>Tab content2</div>
              <div>Tab content3</div>
              <div>Tab content4</div>
              <div>Tab content5</div>
            </FrVrfTab>
          );
        },
      },
    ],
  };

  const frVrfTitle: Component = {
    name: "Title",
    tagName: "<FrVrfTitle>",
    samples: [
      {
        description: "Title (default)",
        Sample: () => {
          return (
            <>
              <FrVrfTitle content="TITLE TITLE TITLE">
                <FrVrfFieldLabel labelContent="UQJP" />
                <FrVrfFieldLabel labelContent="2022-FW" />
              </FrVrfTitle>
            </>
          );
        },
      },
    ],
  };

  const frVrfActionList: Component = {
    name: "Action List",
    tagName: "<FrVrfActionList>",
    samples: [
      {
        description: "Action List (default)",
        Sample: () => {
          const [options, setOptions] = useState([
            {
              label: "Action1",
              value: "action1",
            },
            {
              label: "Action2",
              value: "action2",
            },
          ]);
          const onSelectedOption = (event: CustomEvent) => {};
          return (
            <>
              <FrVrfActionList
                options={options}
                onSelectedOption={onSelectedOption}
              />
            </>
          );
        },
      },
      {
        description: "Action List (icon)",
        Sample: () => {
          const [options, setOptions] = useState([
            {
              label: "Action1",
              value: "action1",
            },
            {
              label: "Action2",
              value: "action2",
            },
          ]);
          const onSelectedOption = (event: CustomEvent) => {};
          return (
            <>
              <FrVrfActionList
                options={options}
                icon="frIcon:edit"
                onSelectedOption={onSelectedOption}
              />
            </>
          );
        },
      },
      {
        description: "Action List (labelContent)",
        Sample: () => {
          const [options, setOptions] = useState([
            {
              label: "Action1",
              value: "action1",
            },
            {
              label: "Action2",
              value: "action2",
            },
          ]);
          const onSelectedOption = (event: CustomEvent) => {};
          return (
            <>
              <FrVrfActionList
                options={options}
                labelContent="TEST"
                onSelectedOption={onSelectedOption}
              />
            </>
          );
        },
      },
      {
        description: "Action List (disabled)",
        Sample: () => {
          const [options, setOptions] = useState([
            {
              label: "Action1",
              value: "action1",
            },
            {
              label: "Action2",
              value: "action2",
            },
          ]);
          const onSelectedOption = (event: CustomEvent) => {};
          return (
            <>
              <FrVrfActionList
                options={options}
                disabled
                onSelectedOption={onSelectedOption}
              />
            </>
          );
        },
      },
    ],
  };

  const frVrfPanel: Component = {
    name: "Panel",
    tagName: "<FrVrfPanel>",
    samples: [
      {
        description: "Panel (default)",
        Sample: () => {
          return (
            <div style={{ margin: "10px 0" }}>
              <FrVrfPanel>
                <FrVrfSingleLineDisplayField
                  label="LABEL1 LABEL1 LABEL1"
                  firstValue="VALUE1 VALUE1 VALUE1"
                />
                <FrVrfSingleLineDisplayField
                  label="LABEL2 LABEL2 LABEL2"
                  firstValue="VALUE2 VALUE2 VALUE2"
                />
                <FrVrfSingleLineDisplayField
                  label="LABEL3 LABEL3 LABEL3"
                  firstValue="VALUE3 VALUE3 VALUE3"
                />
              </FrVrfPanel>
            </div>
          );
        },
      },
      {
        description: "Panel (panelTitle/description)",
        Sample: () => {
          return (
            <div style={{ margin: "10px 0" }}>
              <FrVrfPanel
                panelTitle="TITLE TITLE TITLE"
                description="xxxxx xxxxx xxxxx xxxxx xxxxx"
              >
                <FrVrfSingleLineDisplayField
                  label="LABEL1 LABEL1 LABEL1"
                  firstValue="VALUE1 VALUE1 VALUE1"
                />
                <FrVrfSingleLineDisplayField
                  label="LABEL2 LABEL2 LABEL2"
                  firstValue="VALUE2 VALUE2 VALUE2"
                />
                <FrVrfSingleLineDisplayField
                  label="LABEL3 LABEL3 LABEL3"
                  firstValue="VALUE3 VALUE3 VALUE3"
                />
              </FrVrfPanel>
            </div>
          );
        },
      },
      {
        description: "Panel (theme)",
        Sample: () => {
          return (
            <div style={{ margin: "10px 0" }}>
              <FrVrfPanel theme="primary">
                <FrVrfSingleLineDisplayField
                  label="LABEL1 LABEL1 LABEL1"
                  firstValue="VALUE1 VALUE1 VALUE1"
                />
                <FrVrfSingleLineDisplayField
                  label="LABEL2 LABEL2 LABEL2"
                  firstValue="VALUE2 VALUE2 VALUE2"
                />
                <FrVrfSingleLineDisplayField
                  label="LABEL3 LABEL3 LABEL3"
                  firstValue="VALUE3 VALUE3 VALUE3"
                />
              </FrVrfPanel>
            </div>
          );
        },
      },
      {
        description: "Panel (column)",
        Sample: () => {
          return (
            <div style={{ margin: "10px 0" }}>
              <FrVrfPanel columns={2}>
                <FrVrfSingleLineDisplayField
                  label="LABEL1 LABEL1 LABEL1"
                  firstValue="VALUE1 VALUE1 VALUE1"
                />
                <FrVrfSingleLineDisplayField
                  label="LABEL2 LABEL2 LABEL2"
                  firstValue="VALUE2 VALUE2 VALUE2"
                />
                <FrVrfSingleLineDisplayField
                  label="LABEL3 LABEL3 LABEL3"
                  firstValue="VALUE3 VALUE3 VALUE3"
                />
                <FrVrfSingleLineDisplayField
                  label="LABEL4 LABEL4 LABEL4"
                  firstValue="VALUE4 VALUE4 VALUE4"
                />
                <FrVrfSingleLineDisplayField
                  label="LABEL5 LABEL5 LABEL5"
                  firstValue="VALUE5 VALUE5 VALUE5"
                />
              </FrVrfPanel>
            </div>
          );
        },
      },
    ],
  };

  const frVrfTooltip: Component = {
    name: "Tooltip",
    tagName: "",
    samples: [
      {
        description: "Tooltip (default)",
        Sample: () => {
          return (
            <FrVrfButton
              content="Show Tooltip"
              tooltipVisible={true}
              tooltipContent="This is tooltip. This is tooltip. This is tooltip."
            ></FrVrfButton>
          );
        },
      },
      {
        description: "Tooltip (tooltipPosition)",
        Sample: () => {
          return (
            <FrVrfButton
              content="Show Tooltip"
              tooltipVisible={true}
              tooltipContent="This is tooltip. This is tooltip. This is tooltip."
              tooltipPosition={"below"}
            ></FrVrfButton>
          );
        },
      },
    ],
  };

  const frVrfToast: Component = {
    name: "Toast",
    tagName: "<FrVrfToast>",
    samples: [
      {
        description: "Toast (alert)",
        Sample: () => {
          const onClick = () => {
            showToast({
              design: "alert",
              message: "ERROR OCCURRD.",
            });
          };
          return (
            <FrVrfButton
              content="Show alert toast"
              theme="primary"
              onClicked={onClick}
            />
          );
        },
      },
      {
        description: "Toast (completion)",
        Sample: () => {
          const onClick = () => {
            showToast({
              design: "completion",
              message: "SAVE SUCCESSFULLY.",
            });
          };
          return (
            <FrVrfButton
              content="Show completion toast"
              theme="primary"
              onClicked={onClick}
            />
          );
        },
      },
    ],
  };

  const frVrfAlertBar: Component = {
    name: "Alert Bar",
    tagName: "<FrVrfAlertBar>",
    samples: [
      {
        description: "Alert Bar (default)",
        Sample: () => {
          return (
            <FrVrfAlertBar
              text={
                "Target date should be set before 15 days.\nPlease try again."
              }
            />
          );
        },
      },
      {
        description: "Alert Bar (messageList)",
        Sample: () => {
          return (
            <FrVrfAlertBar
              messageList={[
                {
                  id: "COM_ERR_00001",
                  message: "This record is already rejected.",
                },
                {
                  id: "COM_ERR_00002",
                  message: "Target of processing did not exist.",
                },
              ]}
            />
          );
        },
      },
    ],
  };

  const frVrfFileUploadField: Component = {
    name: "File Upload Field",
    tagName: "<FrVrfFileUploadField>",
    samples: [
      {
        description: "File Upload Field (default)",
        Sample: () => {
          const [path, setPath] = useState("");
          const onFileSelected = useCallback((evt: CustomEvent) => {
            setPath(evt.detail.path);
          }, []);
          return (
            <FrVrfFileUploadField path={path} onFileSelected={onFileSelected} />
          );
        },
      },
      {
        description: "File Upload Field (accept)",
        Sample: () => {
          const [path, setPath] = useState("");
          const onFileSelected = useCallback((evt: CustomEvent) => {
            setPath(evt.detail.path);
          }, []);
          return (
            <FrVrfFileUploadField
              path={path}
              accept=".xlsx"
              onFileSelected={onFileSelected}
            />
          );
        },
      },
      {
        description: "File Upload Field (labelContent)",
        Sample: () => {
          const [path, setPath] = useState("");
          const onFileSelected = useCallback((evt: CustomEvent) => {
            setPath(evt.detail.path);
          }, []);
          return (
            <FrVrfFileUploadField
              path={path}
              labelContent="label label label"
              onFileSelected={onFileSelected}
            />
          );
        },
      },
      {
        description: "File Upload Field (uploadBtnText)",
        Sample: () => {
          const [path, setPath] = useState("");
          const onFileSelected = useCallback((evt: CustomEvent) => {
            setPath(evt.detail.path);
          }, []);
          return (
            <FrVrfFileUploadField
              path={path}
              uploadBtnText="Select File"
              onFileSelected={onFileSelected}
            />
          );
        },
      },
      {
        description: "File Upload Field (disabled)",
        Sample: () => {
          const [path, setPath] = useState("");
          const onFileSelected = useCallback((evt: CustomEvent) => {
            setPath(evt.detail.path);
          }, []);
          return (
            <FrVrfFileUploadField
              path={path}
              disabled
              onFileSelected={onFileSelected}
            />
          );
        },
      },
      {
        description: "File Upload Field (errorMessage)",
        Sample: () => {
          const [path, setPath] = useState("");
          const onFileSelected = useCallback((evt: CustomEvent) => {
            setPath(evt.detail.path);
          }, []);
          return (
            <FrVrfFileUploadField
              path={path}
              errorMessage="Please select the file."
              onFileSelected={onFileSelected}
            />
          );
        },
      },
    ],
  };

  const frVrfLoading: Component = {
    name: "Loading",
    tagName: "<FrVrfLoading>",
    samples: [
      {
        description: "Loading",
        Sample: () => {
          const [loading, setLoading] = useState(false);
          const onClick = () => {
            setLoading(true);
            setTimeout(() => {
              setLoading(false);
            }, 3000);
          };
          return (
            <>
              <FrVrfButton content="Show Loading" onClick={onClick} />
              <FrVrfLoading loading={loading} />
            </>
          );
        },
      },
    ],
  };

  const frVrfHorizontalSeparator: Component = {
    name: "Horizontal Separator",
    tagName: "<FrVrfHorizontalSeparator>",
    samples: [
      {
        description: "Horizontal Separator",
        Sample: () => {
          return (
            <>
              <FrVrfHorizontalSeparator />
              <FrVrfHorizontalSeparator
                style={{ "--vrf-horizontal-separator-height": "3px" }}
              />
              <FrVrfHorizontalSeparator
                style={{ "--vrf-horizontal-separator-height": "4px" }}
              />
            </>
          );
        },
      },
    ],
  };

  const frVrfConfirmDialog: Component = {
    name: "Confirm Dialog",
    tagName: "<FrVrfConfirmDialog>",
    samples: [
      {
        description: "Confirm Dialog (type)",
        Sample: () => {
          return (
            <>
              <FrVrfButton
                content="Show info"
                onClicked={() => {
                  showConfirmationDialog({
                    type: "info",
                    title: "TITLE TITLE TITLE",
                    message:
                      "This is message. This is message. This is message.",
                  });
                }}
              />
              <FrVrfButton
                content="Show confirm"
                onClicked={() => {
                  showConfirmationDialog({
                    type: "confirm",
                    title: "TITLE TITLE TITLE",
                    message:
                      "This is message. This is message. This is message.",
                  });
                }}
              />
              <FrVrfButton
                content="Show warning"
                onClicked={() => {
                  showConfirmationDialog({
                    type: "warning",
                    title: "TITLE TITLE TITLE",
                    message:
                      "This is message. This is message. This is message.",
                  });
                }}
              />
              <FrVrfButton
                content="Show error"
                onClicked={() => {
                  showConfirmationDialog({
                    type: "error",
                    title: "TITLE TITLE TITLE",
                    message:
                      "This is message. This is message. This is message.",
                  });
                }}
              />
              <FrVrfButton
                content="Show success"
                onClicked={() => {
                  showConfirmationDialog({
                    type: "success",
                    title: "TITLE TITLE TITLE",
                    message:
                      "This is message. This is message. This is message.",
                  });
                }}
              />
            </>
          );
        },
      },
      {
        description: "Confirm Dialog (cancelButtonTitle/okButtonTitle)",
        Sample: () => {
          return (
            <>
              <FrVrfButton
                content="cancelButtonTitle/okButtonTitle"
                onClicked={() => {
                  showConfirmationDialog({
                    type: "info",
                    title: "TITLE TITLE TITLE",
                    message:
                      "This is message. This is message. This is message.",
                    cancelButtonTitle: "REJECT",
                    okButtonTitle: "APPROVE",
                  });
                }}
              />
            </>
          );
        },
      },
      {
        description: "Confirm Dialog (showCloseButton)",
        Sample: () => {
          return (
            <>
              <FrVrfButton
                content="showCloseButton"
                onClicked={() => {
                  showConfirmationDialog({
                    type: "info",
                    title: "TITLE TITLE TITLE",
                    message:
                      "This is message. This is message. This is message.",
                    showCloseButton: false,
                  });
                }}
              />
            </>
          );
        },
      },
      {
        description: "Confirm Dialog (showCancelButton)",
        Sample: () => {
          return (
            <>
              <FrVrfButton
                content="showCancelButton"
                onClicked={() => {
                  showConfirmationDialog({
                    type: "info",
                    title: "TITLE TITLE TITLE",
                    message:
                      "This is message. This is message. This is message.",
                    showCancelButton: false,
                  });
                }}
              />
            </>
          );
        },
      },
    ],
  };

  const frVrfListBuilder: Component = {
    name: "List Builder",
    tagName: "<FrVrfListBuilder>",
    samples: [
      {
        description: "List Builder (default)",
        Sample: () => {
          const onClicked = useCallback((evt: CustomEvent) => {
            const itemList = evt.detail.list;
          }, []);
          return (
            <>
              <FrVrfListBuilder
                itemList={WebComponentsConstants.listBuilderItems}
                itemCodeLabel="CODE"
                itemNameLabel="ITEM NAME"
                onItemListSelected={onClicked}
              />
            </>
          );
        },
      },
      {
        description: "List Builder (showHeaderWhenNoResult)",
        Sample: () => {
          const onClicked = useCallback((evt: CustomEvent) => {
            const itemList = evt.detail.list;
          }, []);
          return (
            <>
              <FrVrfListBuilder
                itemList={[]}
                itemCodeLabel="CODE"
                itemNameLabel="ITEM NAME"
                showHeaderWhenNoResult={false}
                onItemListSelected={onClicked}
              />
            </>
          );
        },
      },
      {
        description: "List Builder (columnSetting)",
        Sample: () => {
          const columnSettings: {
            key: string;
            label: string;
            width: number;
            alignment: "left" | "center" | "right";
          }[] = [
            { key: "code", label: "CODE", width: 30, alignment: "center" },
            { key: "name", label: "ITEM", width: 70, alignment: "center" },
          ];
          const onClicked = useCallback((evt: CustomEvent) => {
            const itemList = evt.detail.list;
          }, []);
          return (
            <>
              <FrVrfListBuilder
                itemList={WebComponentsConstants.columnSettingListBuilderItems}
                columnSettings={columnSettings}
              />
            </>
          );
        },
      },
    ],
  };

  const frVrfAccordion: Component = {
    name: "Accordion",
    tagName: "<FrVrfAccordion>",
    samples: [
      {
        description: "Accordion (default)",
        Sample: () => {
          const openedChangeHandle = () => {
            // process.
          };
          return (
            <>
              <FrVrfAccordion
                title="CONTENTS"
                onOpenedChanged={openedChangeHandle}
              >
                <div>Business: UQJP</div>
                <div>GDept: Men's Outer</div>
                <div>Year-Season: 2022FW</div>
              </FrVrfAccordion>
            </>
          );
        },
      },
    ],
  };

  const frVrfTags: Component = {
    name: "Tags",
    tagName: "<FrVrfTags>",
    samples: [
      {
        description: "Tags (default)",
        Sample: () => {
          const [selectedValues, setSelectedValues] = useState<string[]>([""]);
          const onSelected = useCallback((evt: CustomEvent) => {
            setSelectedValues(evt.detail.value);
          }, []);
          return (
            <FrVrfTags
              label="label label label"
              items={WebComponentsConstants.tags}
              selectedValues={selectedValues}
              onSelected={onSelected}
            />
          );
        },
      },
      {
        description: "Tags (labelPosition)",
        Sample: () => {
          const [selectedValues, setSelectedValues] = useState<string[]>([""]);
          const onSelected = useCallback((evt: CustomEvent) => {
            setSelectedValues(evt.detail.value);
          }, []);
          return (
            <FrVrfTags
              label="label label label"
              labelPosition="top"
              items={WebComponentsConstants.tags}
              selectedValues={selectedValues}
              onSelected={onSelected}
            />
          );
        },
      },
      {
        description: "Tags (required)",
        Sample: () => {
          const [selectedValues, setSelectedValues] = useState<string[]>([""]);
          const onSelected = useCallback((evt: CustomEvent) => {
            setSelectedValues(evt.detail.value);
          }, []);
          return (
            <FrVrfTags
              label="label label label"
              required
              items={WebComponentsConstants.tags}
              selectedValues={selectedValues}
              onSelected={onSelected}
            />
          );
        },
      },
      {
        description: "Tags (disabled)",
        Sample: () => {
          const [selectedValues, setSelectedValues] = useState<string[]>([""]);
          const onSelected = useCallback((evt: CustomEvent) => {
            setSelectedValues(evt.detail.value);
          }, []);
          return (
            <FrVrfTags
              label="label label label"
              disabled
              items={WebComponentsConstants.tags}
              selectedValues={selectedValues}
              onSelected={onSelected}
            />
          );
        },
      },
      {
        description: "Tags (errorMessage)",
        Sample: () => {
          const [selectedValues, setSelectedValues] = useState<string[]>([""]);
          const onSelected = useCallback((evt: CustomEvent) => {
            setSelectedValues(evt.detail.value);
          }, []);
          return (
            <FrVrfTags
              label="label label label"
              errorMessage="Select at least one item."
              items={WebComponentsConstants.tags}
              selectedValues={selectedValues}
              onSelected={onSelected}
            />
          );
        },
      },
    ],
  };

  const frVrfFieldLabel: Component = {
    name: "Field Label",
    tagName: "<FrVrfFieldLabel>",
    samples: [
      {
        description: "Field Label (default)",
        Sample: () => {
          return (
            <>
              <FrVrfFieldLabel labelContent="label label label" />
            </>
          );
        },
      },
      {
        description: "Field Label (required)",
        Sample: () => {
          return (
            <>
              <FrVrfFieldLabel labelContent="label label label" required />
            </>
          );
        },
      },
    ],
  };

  const frVrfButtonPanel: Component = {
    name: "Button Panel",
    tagName: "<FrVrfButtonPanel>",
    samples: [
      {
        description: "Button Panel (default)",
        Sample: () => {
          return (
            <>
              <FrVrfButtonPanel>
                <FrVrfButton content="BUTTON1" />
                <FrVrfButton content="BUTTON2" />
                <FrVrfButton content="BUTTON3" />
              </FrVrfButtonPanel>
            </>
          );
        },
      },
      {
        description: "Button Panel (align)",
        Sample: () => {
          return (
            <>
              <FrVrfButtonPanel align="left">
                <FrVrfButton content="BUTTON1" />
                <FrVrfButton content="BUTTON2" />
                <FrVrfButton content="BUTTON3" />
              </FrVrfButtonPanel>
            </>
          );
        },
      },
    ],
  };

  const frVrfColumnPanel: Component = {
    name: "Column Panel",
    tagName: "<FrVrfColumnPanel>",
    samples: [
      {
        description: "Column Panel (default)",
        Sample: () => {
          return (
            <>
              <FrVrfColumnPanel>
                <FrVrfButton content="BUTTON1" />
                <FrVrfButton content="BUTTON2" />
                <FrVrfButton content="BUTTON3" />
              </FrVrfColumnPanel>
            </>
          );
        },
      },
    ],
  };

  const frVrfFormPanel: Component = {
    name: "Form Panel",
    tagName: "<FrVrfFormPanel>",
    samples: [
      {
        description: "Form Panel (default)",
        Sample: () => {
          return (
            <>
              <FrVrfFormPanel>
                <FrVrfButton content="BUTTON1" />
                <FrVrfButton content="BUTTON2" />
                <FrVrfButton content="BUTTON3" />
              </FrVrfFormPanel>
            </>
          );
        },
      },
      {
        description: "Form Panel (columns)",
        Sample: () => {
          return (
            <>
              <FrVrfFormPanel columns={3}>
                <FrVrfButton content="BUTTON1" />
                <FrVrfButton content="BUTTON2" />
                <FrVrfButton content="BUTTON3" />
                <FrVrfButton content="BUTTON4" />
                <FrVrfButton content="BUTTON5" />
                <FrVrfButton content="BUTTON6" />
                <FrVrfButton content="BUTTON7" />
                <FrVrfButton content="BUTTON8" />
                <FrVrfButton content="BUTTON9" />
              </FrVrfFormPanel>
            </>
          );
        },
      },
    ],
  };

  const components = useMemo(() => {
    return [
      frVrfSingleLineInputField,
      frVrfMultipleLineInputField,
      frVrfNumberInputField,
      frVrfSingleLineDisplayField,
      frVrfSingleLinkDisplayField,
      frVrfTextDisplayField,
      frVrfInputDatepickerField,
      frVrfInputDaterangeField,
      frVrfCheckboxField,
      frVrfCheckboxesField,
      frVrfDropdownField,
      frVrfRadioButtonField,
      frVrfSelectorInputField,
      frVrfCodeInputSearchField,
      frVrfMultipleSelectorField,
      frVrfButton,
      frVrfIcon,
      frVrfSearchResultSummary,
      frVrfTab,
      frVrfTitle,
      frVrfActionList,
      frVrfPanel,
      frVrfTooltip,
      frVrfToast,
      frVrfAlertBar,
      frVrfFileUploadField,
      frVrfLoading,
      frVrfHorizontalSeparator,
      frVrfConfirmDialog,
      frVrfListBuilder,
      frVrfAccordion,
      frVrfTags,
      frVrfFieldLabel,
      frVrfButtonPanel,
      frVrfColumnPanel,
      frVrfFormPanel,
    ];
  }, []);

  const [delBtnLeftAsigned, setDelBtnLeftAsigned] = useState(true);
  return (
    <>
      <WebComponentsTable components={components}></WebComponentsTable>
      <FrVrfFooter>
        <FrVrfButton
          slot="left"
          content="DELETE"
          style={{
            "--vrf-button-large-height": "70px",
            "--vrf-button-large-min-width": "160px",
            "--vrf-button-large-margin": "0",
          }}
          theme="caution"
          size="large"
          visible={delBtnLeftAsigned}
        />
        <FrVrfButton
          content="CANCEL"
          style={{
            "--vrf-button-large-height": "70px",
            "--vrf-button-large-min-width": "160px",
            "--vrf-button-large-margin": "0",
          }}
          theme="secondary"
          size="large"
        />
        <FrVrfButton
          content="SAVE"
          style={{
            "--vrf-button-large-height": "70px",
            "--vrf-button-large-min-width": "160px",
            "--vrf-button-large-margin": "0",
          }}
          theme="primary"
          size="large"
        />
      </FrVrfFooter>
    </>
  );
};

const WebComponentsTable = (prop: { components: Component[] }) => {
  return (
    <>
      <table className="component-table" style={{ width: "100%" }}>
        <colgroup>
          <col width={"10%"} />
          <col width={"10%"} />
          <col width={"20%"} />
          <col width={"30%"} />
        </colgroup>
        <thead className="component-table-thead">
          <tr className="component-table-tr">
            <th className="component-table-th">Component</th>
            <th className="component-table-th">Component Tag</th>
            <th className="component-table-th">Description</th>
            <th className="component-table-th">Component Image</th>
          </tr>
        </thead>
        <tbody>
          {prop.components.map((component, index) => {
            return (
              <Fragment key={component.name + index}>
                <tr>
                  <td
                    className="component-table-td"
                    rowSpan={component.samples.length + 1}
                  >
                    {component.name}
                  </td>
                  <td
                    className="component-table-td"
                    rowSpan={component.samples.length + 1}
                  >
                    {component.tagName}
                  </td>
                </tr>
                {component.samples.map((sample, index) => (
                  <tr key={component.name + sample.description + index}>
                    <td className="component-table-td">{sample.description}</td>
                    <td className="component-table-td">
                      <sample.Sample></sample.Sample>
                    </td>
                  </tr>
                ))}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </>
  );
};

interface Component {
  name: string;
  tagName: string;
  samples: {
    description: string;
    Sample: () => JSX.Element;
  }[];
}
